import { motion } from "framer-motion";
import { Reveal, SectionHead } from "@/components/Reveal";
import { Icon } from "@/components/Icon";
import { DIRECTORS } from "@/data/site";

const DirectorBlock = ({ d, index, onPoster }) => {
  const flip = index % 2 === 1;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
      <Reveal className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
        <motion.button
          onClick={() => onPoster(d.image)}
          data-testid={`director-poster-btn-${d.id}`}
          className="block w-full border border-[rgba(111,227,155,0.2)] overflow-hidden"
          whileHover={{ scale: 1.01 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="h-[520px] overflow-hidden">
            <img
              src={d.image}
              alt={d.name}
              className={`h-full object-cover ${d.id === "morena" ? "w-[200%]" : "w-full"}`}
              style={{ objectPosition: d.imageOffset }}
            />
          </div>
        </motion.button>
        <p className="font-mono-label text-[10px] uppercase tracking-[0.18em] mt-4 text-[color:var(--kmg-bone-dim)] opacity-70">
          Tap to view the full leadership profile
        </p>
      </Reveal>

      <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
        <Reveal>
          <h3 className="font-display text-3xl sm:text-4xl" data-testid={`director-name-${d.id}`}>{d.name}</h3>
          <p className="font-mono-label text-[11px] uppercase tracking-[0.18em] mt-4 text-[color:var(--kmg-gold)]">{d.role}</p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="flex flex-wrap gap-2.5 mt-7">
            {d.tags.map((t) => (
              <span key={t} className="rounded-full border border-[rgba(111,227,155,0.28)] px-4 py-1.5 text-xs text-[color:var(--kmg-bone-dim)]">
                {t}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.14}>
          <div className="mt-10 space-y-5">
            {d.bio.map((p) => (
              <p key={p.slice(0, 24)} className="text-base text-[color:var(--kmg-bone-dim)] leading-relaxed max-w-2xl">{p}</p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="eyebrow mt-14">{d.focusLabel}</p>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-7 mt-8">
          {d.focus.map((f, i) => (
            <Reveal key={f.title} delay={0.24 + i * 0.06}>
              <div data-testid={`${d.id}-focus-${i}`}>
                <span className="grid place-items-center w-12 h-12 rounded-full bg-[color:var(--kmg-emerald)] border border-[rgba(111,227,155,0.25)]">
                  <Icon name={f.icon} size={18} className="text-[color:var(--kmg-lime)]" />
                </span>
                <p className="font-display text-sm mt-4 leading-snug">{f.title}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.3}>
          <blockquote className="mt-14 border-l-2 border-[color:var(--kmg-lime)] pl-7" data-testid={`director-vision-${d.id}`}>
            <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[color:var(--kmg-gold)]">Vision</p>
            <p className="font-editorial italic text-xl sm:text-2xl mt-4 leading-snug max-w-xl">"{d.vision}"</p>
          </blockquote>
        </Reveal>
      </div>
    </div>
  );
};

export const Leadership = ({ onPoster }) => (
  <section id="leadership" className="relative py-32 sm:py-40" data-testid="leadership-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHead
        eyebrow="05 — Leadership · Innovation · Impact"
        title="From Farm to Future."
        sub="Meet the directors behind KMG Farm and the KMG Digi Platform."
      />

      <div className="mt-20 space-y-28">
        {DIRECTORS.map((d, i) => (
          <DirectorBlock key={d.id} d={d} index={i} onPoster={onPoster} />
        ))}
      </div>
    </div>
  </section>
);
